<template>
  <div class="global-container">
    <div class="content">
      <!-- Header with Title and Button -->
      <div class="header-section">
        <div class="title-section">
          <h1 class="page-title">Accounting Years</h1>
          <p class="page-description">Manage your financial accounting periods</p>
        </div>
        <router-link to="/settings/accounting-years/new" class="new-year-button">
          <span class="plus-icon">+</span>
          New Accounting Year
        </router-link>
      </div>

      <!-- Accounting Years Grid -->
      <div class="years-grid">
        <div
          v-for="year in accountingYears"
          :key="year.id"
          class="year-card"
          :class="{ 'current-year': year.isCurrent }"
          @click="setActiveYear(year)"
        >
          <!-- Calendar Icon and Year Name -->
          <div class="card-top">
            <div class="year-info">
              <span class="calendar-icon">📅</span>
              <h3 class="year-title">{{ year.name }}</h3>
            </div>
            <span v-if="year.isCurrent" class="current-badge">✓ Current</span>
            <span v-else class="status-label">{{ year.status }}</span>
          </div>

          <!-- Date Range -->
          <p class="date-range">{{ year.dateRange }}</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapState } from 'vuex'

export default {
  name: 'AccountingYear',

  data() {
    return {
    }
  },

    computed: {
    ...mapState('accounting', ['years']),

    accountingYears() {
      return this.years.map(year => ({
    id: year.id,
    name: year.name,
    dateRange: `${this.formatDate(year.startDate)} - ${this.formatDate(year.endDate)}`,
    isCurrent: year.isActive === true,
    status: year.isClosed === true ? 'Closed' : 'Active'
  }))
    }
  },

    methods: {
    ...mapActions('accounting', ['GetAccountingYears', 'SetActiveYear']),
    formatDate(date) {
      const options = { year: 'numeric', month: 'short', day: 'numeric' }
      return new Date(date).toLocaleDateString('en-US', options)
    },
    async setActiveYear(year) {
      if (year.isCurrent) return
      await this.SetActiveYear(year.id)
    }
    },
  created() {
    this.GetAccountingYears()
  }
}

</script>

<style scoped>
.header-section {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 48px;
}

.title-section {
  flex: 1;
}

.page-title {
  font-size: 36px;
  font-weight: 700;
  color: #1f2937;
  margin-bottom: 8px;
}

.page-description {
  font-size: 15px;
  color: #9ca3af;
  font-weight: 400;
}

/* New Year Button */
.new-year-button {
  background: #2563eb;
  color: white;
  border: none;
  padding: 10px 24px;
  border-radius: 8px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: background 0.2s ease;
  white-space: nowrap;
  text-decoration: none;
}

.new-year-button:hover {
  background: #1d4ed8;
}

.plus-icon {
  font-size: 18px;
}

/* Grid Layout */
.years-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 24px;
  width: 100%;
}

/* Year Card */
.year-card {
  background: white;
  border: 2px solid #e5e7eb;
  border-radius: 14px;
  padding: 28px;
  cursor: pointer;
  transition: all 0.3s ease;
}

.year-card:hover {
  border-color: #d1d5db;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
}

.year-card.current-year {
  border-color: #2563eb;
}

/* Card Top Section */
.card-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
}

.year-info {
  display: flex;
  align-items: center;
  gap: 12px;
}

.calendar-icon {
  font-size: 24px;
}

.year-title {
  font-size: 18px;
  font-weight: 700;
  color: #1f2937;
  margin: 0;
}

/* Status Badges */
.current-badge {
  background: #2563eb;
  color: white;
  padding: 6px 14px;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 6px;
}

.status-label {
  background: #9ca3af;
  color: white;
  padding: 6px 14px;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 600;
  text-transform: capitalize;
}

/* Date Range */
.date-range {
  font-size: 14px;
  color: #6b7280;
  font-weight: 500;
  margin: 0;
}

/* Responsive */
@media (max-width: 768px) {
  .header-section {
    flex-direction: column;
    gap: 24px;
  }

  .page-title {
    font-size: 28px;
  }

  .years-grid {
    grid-template-columns: 1fr;
  }
}
</style>
